import { useEffect, useRef, useState } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';

import { RAG_BASE_URL } from '@/config/rag';
import { getAuthToken } from '@/features/auth/session';
import {
  confirmUpload,
  deleteDocument,
  listDocuments,
  presignUpload,
  putToSignedUrl,
  RagApiError,
} from '@/services/ragApi';

import { documentDisplayName } from '../lib/documentName';
import {
  isAcceptedFile,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  resolveContentType,
} from '../lib/uploadConstraints';
import { DEFAULT_VISIBILITY } from '../lib/visibility';
import type { DocumentResponse, UploadQueueItem } from '../types';
import { collectionKeys } from './useCollections';

const POLLING_STATUSES = new Set<DocumentResponse['status']>(['pending', 'processing']);
const STALE_POLLING_MS = 10 * 60 * 1000;

export const documentKeys = {
  list: (collectionId: string) => ['documents', collectionId] as const,
};

/**
 * Invalida as duas visões que mudam juntas quando um documento entra ou sai.
 *
 * A lista de documentos é uma query; os contadores da sidebar e do cabeçalho
 * vêm de outra (`collectionKeys.all`). Invalidar só a primeira deixava a tela
 * mostrando o documento novo com os contadores antigos até o usuário recarregar
 * a página.
 */
function invalidateCollectionViews(qc: QueryClient, collectionId: string) {
  void qc.invalidateQueries({ queryKey: documentKeys.list(collectionId) });
  void qc.invalidateQueries({ queryKey: collectionKeys.all });
}

export function useDocuments(collectionId: string) {
  const qc = useQueryClient();
  const token = getAuthToken();
  const wasPolling = useRef(false);

  const query = useQuery({
    queryKey: documentKeys.list(collectionId),
    queryFn: () => listDocuments(RAG_BASE_URL, token ?? '', collectionId),
    enabled: Boolean(collectionId) && Boolean(token),
    refetchInterval: (query) => {
      const docs = query.state.data;
      if (!docs) return false;
      const hasInFlight = docs.some((d) => POLLING_STATUSES.has(d.status));
      if (!hasInFlight) return false;
      const elapsed = Date.now() - (query.state.dataUpdatedAt ?? 0);
      return elapsed < STALE_POLLING_MS ? 3000 : false;
    },
  });

  // Quando o worker termina, os contadores mudam sem nenhuma ação do usuário —
  // e nada invalidaria a lista de coleções nesse momento. Detecta a virada de
  // "tem documento em processamento" para "não tem" e atualiza os contadores.
  const isPolling = (query.data ?? []).some((d) => POLLING_STATUSES.has(d.status));
  useEffect(() => {
    if (wasPolling.current && !isPolling) {
      void qc.invalidateQueries({ queryKey: collectionKeys.all });
    }
    wasPolling.current = isPolling;
  }, [isPolling, qc]);

  return query;
}

export function useDeleteDocument(collectionId: string) {
  const qc = useQueryClient();
  const token = getAuthToken();
  return useMutation({
    mutationFn: (documentId: string) =>
      deleteDocument(RAG_BASE_URL, token ?? '', collectionId, documentId),
    onSuccess: () => {
      invalidateCollectionViews(qc, collectionId);
      toast.success('Documento removido.');
    },
    onError: (err) => {
      const msg = err instanceof RagApiError ? err.message : 'Erro ao remover documento.';
      toast.error(msg);
    },
  });
}

const MAX_CONCURRENT = 3;

/** Envio pendente de confirmação porque parte dos arquivos já existe na coleção. */
export interface DuplicatePrompt {
  duplicates: File[];
  fresh: File[];
  visibility: 'public' | 'private';
}

async function uploadOne(
  file: File,
  collectionId: string,
  visibility: 'public' | 'private',
  token: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  const presign = await presignUpload(RAG_BASE_URL, token, collectionId, {
    filename: file.name,
    content_type: resolveContentType(file),
    size_bytes: file.size,
    visibility,
  });
  await putToSignedUrl(presign.upload_url, presign.required_headers, file, onProgress);
  await confirmUpload(RAG_BASE_URL, token, collectionId, presign.document_id);
}

export function useUploadQueue(collectionId: string) {
  const qc = useQueryClient();
  const token = getAuthToken();
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [duplicatePrompt, setDuplicatePrompt] = useState<DuplicatePrompt | null>(null);

  function patchItem(id: string, patch: Partial<UploadQueueItem>) {
    setQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  /** Nomes já presentes na coleção, lidos do cache da lista que a tela mostra. */
  function existingNames(): Set<string> {
    const docs = qc.getQueryData<DocumentResponse[]>(documentKeys.list(collectionId)) ?? [];
    return new Set(docs.map((doc) => documentDisplayName(doc).toLowerCase()));
  }

  async function enqueue(files: File[], visibility = DEFAULT_VISIBILITY) {
    const validFiles = files.filter((f) => {
      if (!isAcceptedFile(f)) {
        toast.error(`"${f.name}": apenas PDF, Excel (.xlsx) ou CSV são aceitos.`);
        return false;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`"${f.name}": arquivo muito grande (máx ${MAX_FILE_SIZE_MB} MB).`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    // Duplicata não é erro: reenviar um arquivo é como o usuário atualiza um
    // documento. Mas indexar o mesmo conteúdo duas vezes gera processamento à
    // toa e resposta redundante, então vale confirmar antes.
    const known = existingNames();
    const duplicates = validFiles.filter((f) => known.has(f.name.toLowerCase()));
    if (duplicates.length > 0) {
      setDuplicatePrompt({
        duplicates,
        fresh: validFiles.filter((f) => !known.has(f.name.toLowerCase())),
        visibility,
      });
      return;
    }

    await sendFiles(validFiles, visibility);
  }

  /** Resposta ao aviso de duplicados: enviar todos ou só os que ainda não existem. */
  async function resolveDuplicates(action: 'send-all' | 'skip-duplicates') {
    const prompt = duplicatePrompt;
    setDuplicatePrompt(null);
    if (!prompt) return;

    const files = action === 'send-all' ? [...prompt.fresh, ...prompt.duplicates] : prompt.fresh;
    if (files.length > 0) await sendFiles(files, prompt.visibility);
  }

  async function sendFiles(validFiles: File[], visibility: 'public' | 'private') {
    if (!token) {
      toast.error('Sessão expirada — faça login novamente.');
      return;
    }

    const items: UploadQueueItem[] = validFiles.map((f) => ({
      id: uuid(),
      file: f,
      progress: 0,
      status: 'pending',
    }));

    setQueue((prev) => [...prev, ...items]);

    for (let i = 0; i < items.length; i += MAX_CONCURRENT) {
      const batch = items.slice(i, i + MAX_CONCURRENT);
      await Promise.allSettled(
        batch.map(async (item) => {
          patchItem(item.id, { status: 'uploading' });
          try {
            await uploadOne(item.file, collectionId, visibility, token, (pct) =>
              patchItem(item.id, { progress: pct }),
            );
            patchItem(item.id, { status: 'done', progress: 100 });
          } catch (err) {
            const msg = err instanceof RagApiError ? err.message : 'Falha no upload.';
            patchItem(item.id, { status: 'error', error: msg });
            toast.error(`"${item.file.name}": ${msg}`);
          }
        }),
      );
    }

    invalidateCollectionViews(qc, collectionId);
    setQueue((prev) => prev.filter((i) => i.status !== 'done'));
  }

  function dismiss(id: string) {
    setQueue((prev) => prev.filter((i) => i.id !== id));
  }

  return { queue, enqueue, dismiss, duplicatePrompt, resolveDuplicates };
}
